import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import pollingSlice, { deletePollById, fetchPollQuestions, getSpeakerPolls } from 'store/polling'
import {
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Tooltip,
  Typography
} from '@material-ui/core'
import { colors } from 'utils/colors'
import { Box } from '@mui/material'
import { Delete, Edit, Warning } from '@material-ui/icons'
import PollingForm from 'views/admin/subjects/PollingForm/PollingForm'
import ProgressButton from 'components/ProgressButton'
import { sortBy, isEqual } from 'lodash'
import { nanoid } from 'nanoid'
import { toast } from 'utils/notify'

const emptyQuestion = {
  title: '',
  options: [
    { optId: nanoid(), opt: '' },
    { optId: nanoid(), opt: '' }
  ]
}

const pollInitialValues = {
  title: '',
  questions: [emptyQuestion]
}

const PollingListing = ({ speakerId, forApproval }) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)
  const [editedPollId, setEditedPollId] = useState('')
  const [editedPollStatus, setEditedPollStatus] = useState('')
  const [deletedPollId, setDeletedPollId] = useState('')
  const [questionLoading, setQuestionLoading] = useState(false)
  const [pollsLoading, setPollsLoading] = useState(false)
  const [pollValues, setPollValues] = useState(pollInitialValues)

  const { user } = useSelector((state) => state.auth)
  const { polls, inProgress } = useSelector((state) => state.polling)

  const handleCloseModal = (formValues) => {
    if (isEqual(pollValues, formValues)) {
      setIsModalOpen(false)
      setEditedPollId('')
      setEditedPollStatus('')
      setPollValues(pollInitialValues)
    } else {
      setConfirmModal(true)
    }
  }
  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseConfirmModal = () => {
    setConfirmModal(false)
    setIsModalOpen(false)
    setEditedPollId('')
    setEditedPollStatus('')
    setPollValues(pollInitialValues)
  }

  const handleOpenModalForEdit = async (poll) => {
    setIsModalOpen(true)
    setEditedPollId(poll?.id)
    setEditedPollStatus(poll?.status)
    setQuestionLoading(true)
    if (!speakerId) {
      setPollValues({
        title: poll?.title,
        questions: poll?.questions
      })
    } else {
      const questions = await fetchPollQuestions(poll?.id)
      questions.forEach((question) => sortBy(question.options, 'order'))
      setPollValues({
        title: poll?.title,
        questions: questions
      })
    }
    setQuestionLoading(false)
  }

  const handleDeletePoll = async () => {
    const isDeleted = await dispatch(deletePollById(deletedPollId, { validateChecks: true }))
    isDeleted && toast.success('Poll and questions deleted.')
    setDeletedPollId('')
    setEditedPollStatus('')
  }

  useEffect(() => {
    if (speakerId) {
      setPollsLoading(true)
      const unsub = getSpeakerPolls(user?.conferenceId, speakerId, (data) => {
        dispatch(pollingSlice.actions.setPolls(data))
        setPollsLoading(false)
      })
      return () => unsub && unsub()
    }
  }, [user?.conferenceId, speakerId])

  return (
    <Box>
      <Card elevation={0} className={classes.pollsSection}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography className={classes.cardHeading}>Available Polls</Typography>
          <Button variant="contained" color="primary" onClick={handleOpenModal}>
            Add Poll
          </Button>
        </Box>
        {pollsLoading ? (
          <Box
            sx={{
              margin: '16px 0px',
              height: '100px',
              display: 'flex',
              justifyContent: 'center'
            }}>
            <CircularProgress />
          </Box>
        ) : !polls.length ? (
          <Typography className={classes.notFoundPolls}>No Poll Exists.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {polls.map((poll) => (
              <Card key={poll?.id} className={classes.pollCard}>
                <Typography>{poll?.title}</Typography>
                <Box>
                  <Tooltip title="Edit">
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => handleOpenModalForEdit(poll)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => setDeletedPollId(poll?.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </Card>

      <Dialog
        open={isModalOpen}
        maxWidth={false}
        PaperProps={{ className: classes.pollDialogPaper }}>
        <DialogContent className={classes.pollModalContent}>
          {questionLoading ? (
            <Box
              sx={{
                margin: '16px 0px',
                height: '100px',
                display: 'flex',
                justifyContent: 'center'
              }}>
              <CircularProgress />
            </Box>
          ) : (
            <PollingForm
              forApproval={forApproval}
              editedPollId={editedPollId}
              pollStatus={editedPollStatus}
              pollValues={pollValues}
              speakerId={speakerId}
              conferenceId={user?.conferenceId}
              handleCloseModal={handleCloseModal}
              handleCloseConfirmModal={handleCloseConfirmModal}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" open={confirmModal} onClose={() => setConfirmModal(false)}>
        <Box className={classes.warningModalHeader}>
          <Warning className={classes.warningIcon} />
          <Typography className={classes.warningTitle}>Warning</Typography>
        </Box>
        <DialogContent className={classes.modalContent}>
          <Typography variant="body2">
            Any unsaved data will be lost. Press CANCEL to go back and save these changes, or OK to
            discard these changes.
          </Typography>

          <Box className={classes.actions}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleCloseConfirmModal}>
              ok
            </Button>
            <Button size="medium" onClick={() => setConfirmModal(false)}>
              cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog fullWidth maxWidth="sm" open={Boolean(deletedPollId)}>
        <DialogTitle>Delete Poll</DialogTitle>
        <DialogContent className={classes.modalContent}>
          <Typography>
            Are you sure you want to delete this Poll? This will also remove all questions within
            this Poll.
          </Typography>

          <Box className={classes.actions}>
            <Button
              size="small"
              variant="contained"
              disabled={inProgress}
              onClick={() => setDeletedPollId('')}>
              No
            </Button>
            <ProgressButton
              size="small"
              variant="contained"
              color="primary"
              disabled={inProgress}
              inProgress={inProgress}
              label="Yes"
              onClick={handleDeletePoll}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default PollingListing

const useStyles = makeStyles({
  warningModalHeader: {
    padding: '20px 20px 10px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  pollDialogPaper: {
    width: '92vw',
    maxWidth: '1400px',
    height: '92vh',
    maxHeight: '92vh'
  },
  pollModalContent: {
    overflowX: 'hidden',
    overflowY: 'hidden',
    padding: '0px 30px 30px !important'
  },
  warningTitle: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  warningIcon: {
    color: '#ffa726'
  },
  actions: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '10px'
  },
  pollCard: {
    padding: '10px 30px',
    boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  cardHeading: {
    color: colors.indigo,
    fontSize: '18px'
  },
  addBtn: {
    textTransform: 'none'
  },
  addQuestionBtn: {
    width: '100%',
    textTransform: 'none'
  },
  pollBtnBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginTop: '16px',
    justifyContent: 'flex-end',
    paddingBottom: '16px'
  },
  pollsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    border: '1px solid #bdbdbd',
    borderRadius: '10px',
    padding: '16px'
  },
  notFoundPolls: {
    textAlign: 'center',
    fontSize: '14px'
  },
  iconButton: {
    padding: '8px'
  },
  modalContent: {
    padding: '4px 16px 4px 22px'
  }
})
